import React, { useCallback, useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, Marker, useMapEvents } from "react-leaflet";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "./ParcelsPage.css";

// Parcels from backend/analysis/data/Parcel.csv (attributes only), drawn with the shapes of the
// existing parcel layer. Zoomed out the server returns clusters; from shapes_min_zoom, shapes.
const API = (process.env.REACT_APP_API_BASE || "") + "/api/analysis/parcels-csv";
const YORK_CENTER = [43.98, -79.45];
const START_ZOOM = 10;
const DEFAULT_SHAPES_MIN_ZOOM = 16;

const PARCEL_STYLE = { color: "#1d4ed8", weight: 1, opacity: 0.9, fillColor: "#60a5fa", fillOpacity: 0.25 };
const SELECTED_STYLE = { color: "#b91c1c", weight: 3, opacity: 1, fillColor: "#f87171", fillOpacity: 0.45 };

const fmtNumber = (v) => Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmtDate = (v) => (v ? v.slice(0, 10).replace(/\//g, "-") : "");
const fmtProDate = (v) => (v && v.length === 8 ? `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}` : v);
const fmtArea = (v) => {
    const m2 = Number(v);
    return m2 >= 10000 ? `${fmtNumber(m2)} m² (${(m2 / 10000).toFixed(2)} ha)` : `${fmtNumber(m2)} m²`;
};

const FIELDS = [
    ["LOCATION", "Address"],
    ["MUNNAME", "Municipality"],
    ["PAR_GIS_ID", "Parcel ID"],
    ["PLANNUM", "Plan number"],
    ["PAR_TYPE", "Parcel type"],
    ["LIFESTATUS", "Status"],
    ["SHAPE_Area", "Area", fmtArea],
    ["SHAPE_Length", "Perimeter", (v) => `${fmtNumber(v)} m`],
    ["TOT_AROLL", "Assessment roll numbers"],
    ["TOT_PIN", "Property PINs"],
    ["TOT_ADDRS", "Addresses"],
    ["CREDATE", "Created", fmtDate],
    ["MODDATE", "Last modified", fmtDate],
    ["MODTYPE", "Modification type"],
    ["PRO_DATE", "Processed", fmtProDate],
    ["SOURCE", "Source"],
    ["SOURCETYPE", "Source type"],
    ["OBJECTID", "Object ID"],
    ["YK_GUI_ID", "York GUID"],
];

function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));
}

function clusterIcon(count) {
    const label = count >= 1000 ? `${(count / 1000).toFixed(count >= 10000 ? 0 : 1)}k` : String(count);
    const size = count >= 10000 ? 46 : count >= 1000 ? 40 : count >= 100 ? 34 : 28;
    return L.divIcon({
        className: "parcels-cluster-icon",
        html: `<div class="parcels-cluster" style="width:${size}px;height:${size}px">${label}</div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
    });
}

function ViewWatcher({ onViewChange }) {
    const map = useMapEvents({
        moveend: () => onViewChange(map),
    });

    useEffect(() => {
        onViewChange(map);
    }, [map, onViewChange]);

    return null;
}

function ClusterMarkers({ items }) {
    const map = useMapEvents({});
    return items.map((item) => (
        <Marker
            key={`${item.lat}:${item.lng}`}
            position={[item.lat, item.lng]}
            icon={clusterIcon(item.count)}
            eventHandlers={{
                click: () => map.setView([item.lat, item.lng], Math.min(map.getZoom() + 2, 18)),
            }}
        />
    ));
}

function SummaryPanel({ summary, error }) {
    if (error) {
        return (
            <div className="parcels-page__message parcels-page__message--error" role="alert">
                <strong>Could not load the parcel data.</strong>
                <div>{error}. Please refresh the page to try again.</div>
            </div>
        );
    }
    if (!summary) {
        return <div className="parcels-page__message" role="status">Loading parcel data…</div>;
    }

    return (
        <>
            <div className="parcels-page__metrics">
                <div className="parcels-page__metric">
                    <span>Parcels</span>
                    <strong>{fmtNumber(summary.parcels)}</strong>
                </div>
                <div className="parcels-page__metric">
                    <span>On the map</span>
                    <strong>{fmtNumber(summary.with_shape)}</strong>
                </div>
                <div className="parcels-page__metric">
                    <span>Without a shape</span>
                    <strong>{fmtNumber(summary.without_shape)}</strong>
                </div>
            </div>

            <p className="parcels-page__note">
                Source: <strong>{summary.file}</strong> (York Region parcel attributes, processed up to {summary.processed_up_to}).
                Shapes come from the site&apos;s existing parcel layer, so the {fmtNumber(summary.without_shape)} parcels that
                are only in this file cannot be drawn yet.
                {summary.duplicate_rows > 0 && ` ${fmtNumber(summary.duplicate_rows)} repeated rows were merged into their newest version.`}
            </p>

            <p className="parcels-page__hint">
                Zoom in to level {summary.shapes_min_zoom} to see parcel shapes, then click a parcel for its details.
            </p>

            <h3 className="parcels-page__section-title">Parcels by municipality</h3>
            <div className="parcels-page__list">
                {summary.municipalities.map((m) => (
                    <div key={m.name} className="parcels-page__row">
                        <span>{m.name}</span>
                        <strong>{fmtNumber(m.parcels)}</strong>
                    </div>
                ))}
            </div>
        </>
    );
}

function DetailsPanel({ details, loading, error, onBack }) {
    const navigate = useNavigate();
    const attributes = details?.attributes || {};

    return (
        <>
            <button type="button" className="parcels-page__back" onClick={onBack}>
                ← Back to overview
            </button>
            <h3 className="parcels-page__section-title">
                {attributes.LOCATION || (details ? `Parcel ${details.parcel_id}` : "Parcel details")}
            </h3>

            {loading && <div className="parcels-page__message" role="status">Loading parcel details…</div>}
            {error && (
                <div className="parcels-page__message parcels-page__message--error" role="alert">
                    {error}
                </div>
            )}

            {details && !details.has_shape && (
                <div className="parcels-page__message">This parcel is only in Parcel.csv, so it has no shape on the map yet.</div>
            )}

            {details && (
                <div className="parcels-page__list">
                    {FIELDS.map(([key, label, format]) => {
                        const raw = (attributes[key] || "").trim();
                        return (
                            <div key={key} className="parcels-page__row">
                                <span>{label}</span>
                                <strong>{raw ? (format ? format(raw) : raw) : "-"}</strong>
                            </div>
                        );
                    })}
                </div>
            )}

            {details && (
                <button
                    type="button"
                    className="parcels-page__analyze"
                    disabled={!details.has_shape}
                    title={details.has_shape ? undefined : "This parcel has no shape to analyze yet."}
                    onClick={() => navigate(`/projects/parcel/${details.parcel_id}`)}
                >
                    Analyze Project →
                </button>
            )}
        </>
    );
}

export default function ParcelsPage() {
    const [summary, setSummary] = useState(null);
    const [summaryError, setSummaryError] = useState(null);
    const [mapData, setMapData] = useState(null);
    const [mapError, setMapError] = useState(null);
    const [loadingMap, setLoadingMap] = useState(false);
    const [viewZoom, setViewZoom] = useState(START_ZOOM);
    const [selectedId, setSelectedId] = useState(null);
    const [details, setDetails] = useState(null);
    const [detailsError, setDetailsError] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    const requestRef = useRef(0);
    const debounceRef = useRef(null);
    const selectedIdRef = useRef(null);
    const selectedLayerRef = useRef(null);

    useEffect(() => {
        fetch(`${API}/summary/`)
            .then(async (r) => {
                const d = await r.json();
                if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
                setSummary(d);
            })
            .catch((e) => {
                console.error("Parcels summary error:", e);
                setSummaryError(e.message || String(e));
            });
    }, []);

    useEffect(() => () => clearTimeout(debounceRef.current), []);

    const onViewChange = useCallback((map) => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            const bounds = map.getBounds();
            const zoom = map.getZoom();
            const id = ++requestRef.current;
            setViewZoom(zoom);
            setLoadingMap(true);

            try {
                const params = new URLSearchParams({
                    minx: bounds.getWest(),
                    miny: bounds.getSouth(),
                    maxx: bounds.getEast(),
                    maxy: bounds.getNorth(),
                    zoom,
                });
                const r = await fetch(`${API}/map-data/?${params}`);
                const d = await r.json();
                if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
                if (id !== requestRef.current) return; // a newer view was requested meanwhile
                selectedLayerRef.current = null;
                setMapData({ ...d, requestId: id });
                setMapError(null);
            } catch (e) {
                if (id === requestRef.current) {
                    console.error("Parcels map data error:", e);
                    setMapError(e.message || String(e));
                }
            } finally {
                if (id === requestRef.current) setLoadingMap(false);
            }
        }, 250);
    }, []);

    const selectParcel = async (parcelId) => {
        selectedIdRef.current = parcelId;
        setSelectedId(parcelId);
        setDetails(null);
        setDetailsError(null);
        setLoadingDetails(true);

        try {
            const r = await fetch(`${API}/parcel/${parcelId}/`);
            const d = await r.json();
            if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
            if (selectedIdRef.current === parcelId) setDetails(d);
        } catch (e) {
            if (selectedIdRef.current === parcelId) setDetailsError(e.message || String(e));
        } finally {
            if (selectedIdRef.current === parcelId) setLoadingDetails(false);
        }
    };

    const clearSelection = () => {
        selectedLayerRef.current?.setStyle(PARCEL_STYLE);
        selectedLayerRef.current = null;
        selectedIdRef.current = null;
        setSelectedId(null);
        setDetails(null);
        setDetailsError(null);
    };

    const parcelStyle = (feature) =>
        feature.properties.PAR_GIS_ID === selectedIdRef.current ? SELECTED_STYLE : PARCEL_STYLE;

    const onEachParcel = (feature, layer) => {
        const { PAR_GIS_ID: parcelId, LOCATION: location } = feature.properties;
        if (parcelId === selectedIdRef.current) selectedLayerRef.current = layer;

        layer.bindTooltip(escapeHtml(location || `Parcel ${parcelId}`), { sticky: true });
        layer.on("click", () => {
            selectedLayerRef.current?.setStyle(PARCEL_STYLE);
            layer.setStyle(SELECTED_STYLE);
            selectedLayerRef.current = layer;
            selectParcel(parcelId);
        });
    };

    const shapesMinZoom = summary?.shapes_min_zoom || DEFAULT_SHAPES_MIN_ZOOM;

    return (
        <div className="parcels-page">
            <h2 className="parcels-page__title">York Region Parcels</h2>
            <p className="parcels-page__subtitle">Parcel attributes from Parcel.csv on the York Region parcel map.</p>

            <div className="parcels-page__layout">
                <aside className="parcels-page__panel">
                    {selectedId != null ? (
                        <DetailsPanel details={details} loading={loadingDetails} error={detailsError} onBack={clearSelection} />
                    ) : (
                        <SummaryPanel summary={summary} error={summaryError} />
                    )}
                </aside>

                <div className="parcels-page__map">
                    <div className="parcels-page__map-status">
                        {mapError ? (
                            <span className="parcels-page__chip parcels-page__chip--error" role="alert">
                                Could not load the map data ({mapError}). Move the map to try again.
                            </span>
                        ) : loadingMap ? (
                            <span className="parcels-page__chip" role="status">Loading map…</span>
                        ) : mapData?.mode === "parcels" && mapData.truncated ? (
                            <span className="parcels-page__chip">
                                Showing the {fmtNumber(mapData.returned_count)} parcels nearest the centre of {fmtNumber(mapData.total_in_view)}. Zoom in to see all.
                            </span>
                        ) : viewZoom < shapesMinZoom ? (
                            <span className="parcels-page__chip">Zoom in to level {shapesMinZoom} to see parcel shapes (now {viewZoom}).</span>
                        ) : null}
                    </div>

                    <MapContainer center={YORK_CENTER} zoom={START_ZOOM} style={{ height: "100%", width: "100%" }}>
                        <TileLayer
                            attribution="&copy; OpenStreetMap contributors"
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <ViewWatcher onViewChange={onViewChange} />

                        {mapData?.mode === "clusters" && <ClusterMarkers items={mapData.items} />}

                        {mapData?.mode === "parcels" && (
                            <GeoJSON
                                key={mapData.requestId}
                                data={mapData}
                                style={parcelStyle}
                                onEachFeature={onEachParcel}
                            />
                        )}
                    </MapContainer>
                </div>
            </div>
        </div>
    );
}
